import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  EventEmitter,
  inject,
  Input,
  input,
  OnInit,
  Output,
  signal,
  WritableSignal,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { SVGIconModule } from '@progress/kendo-angular-icons';
import { TextBoxModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { NotificationService } from '@progress/kendo-angular-notification';
import { SVGIcon } from '@progress/kendo-svg-icons';
import { Observable } from 'rxjs';
import { DynamicFormBuilder } from '../../services/dynamic-form-builder.service';

export type TestConnectionHandler = (
  organizationId: number,
  formId: number,
  formValues: Record<string, any>
) => Observable<any>;

@Component({
  selector: 'app-dynamic-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonsModule, LabelModule, TextBoxModule, SVGIconModule],
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.scss'],
})
export class DynamicFormComponent implements OnInit {
  @Input() formGroup!: FormGroup;

  public _formId = 0;
  @Input()
  public set formId(value: number) {
    if (value !== this._formId && value) {
      this._formId = value;
      this.loadFormForIntegration();
    }
  }
  public get formId(): number {
    return this._formId;
  }

  @Input() title: string = 'Authentication';
  @Input() loading!: WritableSignal<boolean>;
  @Input() icons: Record<string, SVGIcon> = {};

  @Input() testSuccess!: WritableSignal<boolean>;

  @Output() testConnection = new EventEmitter<boolean>(false);
  @Output() saveConfiguration = new EventEmitter<any>();

  public formControls = signal<{ key: string; config: any }[]>([]);
  public organizationId = input.required<number | null>();
  public testConnectionHandler = input.required<TestConnectionHandler>();

  public lastTestedFormValues: any = null;

  private dynamicFormBuilder = inject(DynamicFormBuilder);
  private notificationService = inject(NotificationService);

  constructor() {
    effect(() => {
      if (!this.organizationId()) return;
      this.loadFormForIntegration();
    });
  }

  ngOnInit() {}

  private loadFormForIntegration(): void {
    if (!this.formId) return;
    this.dynamicFormBuilder.createFormForIntegration(this.formId).subscribe((result) => {
      this.formGroup = result.formGroup;
      this.formControls.set(result.controls);
    });
  }

  public onTestConnection(): void {
    const orgId = this.organizationId();
    if (!orgId) return;

    this.loading.set(true);
    const formValues = this.formGroup.value;

    this.testConnectionHandler()(orgId, this._formId, formValues).subscribe({
      next: () => this.onTestSuccess(formValues),
      error: () => this.onTestFailure(),
    });
  }

  private onTestSuccess(formValues: any): void {
    this.testSuccess.set(true);
    this.loading.set(false);
    this.lastTestedFormValues = { ...formValues };
    this.testConnection.emit(true);
    this.notificationService.show({
      content: 'Connection test successful',
      cssClass: 'notification-success',
      animation: { type: 'slide', duration: 400 },
      hideAfter: 3000,
      position: { horizontal: 'right', vertical: 'top' },
      type: { style: 'success', icon: true },
    });
  }

  private onTestFailure(): void {
    this.testSuccess.set(false);
    this.loading.set(false);
    this.lastTestedFormValues = null;
    this.testConnection.emit(false);
  }

  public onSaveConfiguration(): void {
    if (!this.organizationId() || !this.testSuccess() || !this.lastTestedFormValues) return;

    if (this.hasFormChangedSinceTest()) {
      this.testSuccess.set(false);
      this.lastTestedFormValues = null;
      return;
    }

    this.saveConfiguration.emit(this.lastTestedFormValues);
  }

  public hasFormChangedSinceTest(): boolean {
    if (!this.lastTestedFormValues || !this.testSuccess()) return false;
    const currentFormValues = this.formGroup?.value;
    return JSON.stringify(currentFormValues) !== JSON.stringify(this.lastTestedFormValues);
  }

  public getCheckboxLabel(controlKey: string): string {
    if (controlKey.toLowerCase().includes('multitenant') || controlKey.toLowerCase().includes('multi_tenant')) {
      return 'Supports Multi-Tenant';
    }
    return controlKey.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
  }
}
